import { useEffect } from "react";
import { Link } from "react-router-dom";
import { AddHeader } from "../../components/AddHeader";
import { useTranslation } from "react-i18next";
import { useBrand } from "../../context/BrandContext";
import {
  TERMS_VERSION,
  PRIVACY_CONTACT,
  PING4ALL_ADDRESS,
} from "../../constants/legalPolicy";
import { LegalFooter } from "../../components/legal/LegalFooter";
import "./PrivacyNotice.css";

export default function TermsOfUsePage() {
  const { t } = useTranslation();
  const { brand } = useBrand();
  const club = brand?.brand_name || t("yourClub", "Your club");

  useEffect(() => {
    document.title = t("terms", "Terms of use") + " · SmartTT";
  }, [t]);

  return (
    <div className="terms-page">
      <AddHeader title={t("terms", "Terms of use")} />
      <article className="terms-page__body">
        <p className="terms-page__meta">
          {t("privacyVersion", "Version")} {TERMS_VERSION}
          {" · "}
          {t("termsUpdated", "Last updated 31 August 2026")}
        </p>
        <p>
          {club}{" "}
          {t(
            "termsIntro",
            "gives you access to SmartTT, built by Ping4All. These terms govern use of the web app and related club tools. By creating an account or using SmartTT, you agree to them. If you do not agree, do not use the service."
          )}
        </p>

        <h2>{t("termsPartiesTitle", "Who these terms are between")}</h2>
        <ul>
          <li>
            {t(
              "termsPartiesClub",
              "Your club provides SmartTT to its members, coaches, administrators and invited guests."
            )}
          </li>
          <li>
            {t("termsPartiesPing4All", "Ping4All operates the software on the club's instructions.")}{" "}
            {PING4ALL_ADDRESS}.{" "}
            <a href={`mailto:${PRIVACY_CONTACT}`}>{PRIVACY_CONTACT}</a>
          </li>
          <li>
            {t(
              "termsPartiesContract",
              "Your membership, if any, is a contract with your club. These terms cover use of the software only."
            )}
          </li>
        </ul>

        <h2>{t("termsServiceTitle", "The service")}</h2>
        <p>
          {t(
            "termsServiceBody",
            "SmartTT helps the club plan training, record sessions, set a season result and SMART training objectives, and follow progress. Optional features — training video, movement analysis, and heart-rate measurement — need a separate consent. Pingflo, the in-app assistant, can suggest a training focus. Those suggestions are not decisions."
          )}
        </p>

        <h2>{t("termsAccountTitle", "Your account")}</h2>
        <ul>
          <li>
            {t(
              "termsAccountBasis",
              "Creating an account concludes a contract for the use of SmartTT. Account and training records are processed because they are necessary for that contract (GDPR Art. 6(1)(b)). Video and heart rate remain optional and need separate consent."
            )}
          </li>
          <li>
            {t(
              "termsAccountPassword",
              "Keep your password confidential. One person per account; do not share logins."
            )}
          </li>
          <li>
            {t(
              "termsAccountMinors",
              "Under 18: one parent or legal guardian accepts these terms so the child can have an account. Coach video, public image and heart rate are separate optional consents in the app. A paper form is only a fallback. A player can train fully without being recorded."
            )}
          </li>
        </ul>

        <h2>{t("termsRecordingTitle", "Video, sensors and image")}</h2>
        <p>
          {t(
            "termsRecordingBody",
            "Coach video, public image and heart rate are optional and separate. Refusing them does not stop you training with the club. Adults grant or withdraw in their profile. Under 18, one linked parent grants or withdraws in the Parent Hub. A paper form is only a fallback. Agreeing to these terms does not grant any of those purposes."
          )}
        </p>

        <h2>{t("termsAiTitle", "AI and measurements")}</h2>
        <p>
          {t(
            "termsAiBody",
            "When text is written by AI, the product tells you. Stroke counts, movement measurements and training suggestions are estimates for coaching. They are not medical advice, a diagnosis, or a fitness-to-play decision. A coach reviews them. See a doctor for anything health-related. SmartTT does not automatically rank you, select you for competition, or exclude you from the club."
          )}
        </p>

        <h2>{t("termsUseTitle", "Acceptable use")}</h2>
        <p>{t("termsUseIntro", "You must not:")}</p>
        <ul>
          <li>
            {t(
              "termsUse1",
              "Record, copy or share another person's video or personal data without the club's authorisation"
            )}
          </li>
          <li>
            {t(
              "termsUse2",
              "Access another account, interfere with security, or scrape the service"
            )}
          </li>
          <li>{t("termsUse3", "Upload unlawful, abusive or infringing content")}</li>
          <li>
            {t(
              "termsUse4",
              "Treat measurements or AI output as medical or legal advice"
            )}
          </li>
          <li>
            {t(
              "termsUse5",
              "Use SmartTT to advertise, sell player data, or contact members for unrelated commercial purposes"
            )}
          </li>
        </ul>

        <h2>{t("termsContentTitle", "Your content")}</h2>
        <p>
          {t(
            "termsContentBody",
            "You keep the rights in photos and text you submit (for a child, the guardian does). You grant the club and Ping4All a licence to store and display that content as needed to run SmartTT for your club. Hall video is processed only with the relevant consent."
          )}
        </p>

        <h2>{t("termsIpTitle", "Software and branding")}</h2>
        <p>
          {t(
            "termsIpBody",
            "SmartTT software, design and documentation remain Ping4All's. Club names, logos and colours remain the club's. Subject to these terms, Ping4All grants you a revocable, non-exclusive, non-transferable licence to use SmartTT for your club role, on the web and in the mobile apps. That licence ends when your account ends. All rights not granted stay with Ping4All and the club."
          )}
        </p>

        <h2>{t("termsBookingTitle", "Booking and payments")}</h2>
        <p>
          {t(
            "termsBookingBody",
            "If the club enables table booking or payments, the booking contract and refunds are with the club. Ping4All only provides the software. Card payments, where used, are processed by the provider the club enabled. These terms are not a paid subscription with Ping4All. The iOS and Android apps may use extra mobile data; those charges are yours."
          )}
        </p>

        <h2>{t("termsAvailabilityTitle", "Availability")}</h2>
        <p>
          {t(
            "termsAvailabilityBody",
            "We aim to keep SmartTT available but do not guarantee uninterrupted access. Features may change or be interrupted. Optional video or sensor tools may be unavailable at a given session. Ping4All may change, suspend or discontinue a part of the service."
          )}
        </p>

        <h2>{t("termsWarrantyTitle", "No warranty")}</h2>
        <p>
          {t(
            "termsWarrantyBody",
            "SmartTT and related content are provided as is and as available. Ping4All does not warrant that the service is error-free, complete, or fit for a particular training outcome. Coaching suggestions, measurements and AI text are estimates. Mandatory warranties required by law still apply."
          )}
        </p>

        <h2>{t("termsLiabilityTitle", "Liability")}</h2>
        <p>
          {t(
            "termsLiabilityBody",
            "Your use of SmartTT is at your own risk as regards training results. To the extent allowed by law, Ping4All is not liable for decisions the club or a coach makes using the software, or for:"
          )}
        </p>
        <ul>
          <li>{t("termsLiability1", "loss of profits, business, revenue or anticipated savings")}</li>
          <li>{t("termsLiability2", "loss or corruption of data")}</li>
          <li>{t("termsLiability3", "loss of goodwill or any indirect or consequential loss")}</li>
        </ul>
        <p>
          {t(
            "termsLiabilityCarve",
            "Nothing in these terms limits liability for death or personal injury caused by Ping4All's negligence, for fraud, or for any matter the law does not allow us to limit. Mandatory consumer and data-protection rights are not limited."
          )}{" "}
          <Link to="/privacy">{t("privacyPolicy", "Privacy notice")}</Link>.
        </p>

        <h2>{t("termsIndemnityTitle", "If you use SmartTT for a club")}</h2>
        <p>
          {t(
            "termsIndemnityBody",
            "If you use SmartTT as a coach or administrator for a club, you will indemnify Ping4All against claims, losses and reasonable legal costs arising from your breach of these terms or of the law, to the extent allowed. This does not require a consumer or a child to indemnify Ping4All beyond what the law permits."
          )}
        </p>

        <h2>{t("termsEndTitle", "Suspension and end of use")}</h2>
        <p>
          {t(
            "termsEndBody",
            "The club or Ping4All may suspend an account that breaches these terms or puts others at risk. You can ask your club to close your account. After closure, data is kept or deleted as described in the privacy notice."
          )}
        </p>

        <h2>{t("termsChangesTitle", "Changes")}</h2>
        <p>
          {t(
            "termsChangesBody",
            "When these terms change, we update the version and date. Material changes will be shown in the app or sent by email. Continued use after the effective date means you accept the new terms, unless the law requires a fresh agreement."
          )}
        </p>

        <h2>{t("termsBoilerplateTitle", "Other terms")}</h2>
        <ul>
          <li>
            {t(
              "termsBoilerplateAssign",
              "Ping4All may transfer these terms to another organisation; your rights stay the same. You may transfer yours only with Ping4All's written agreement."
            )}
          </li>
          <li>
            {t(
              "termsBoilerplateEntire",
              "These terms, the privacy notice and, for the mobile apps, the EULA are the whole agreement for use of SmartTT."
            )}
          </li>
          <li>
            {t(
              "termsBoilerplateWaiver",
              "If Ping4All does not enforce a term, it can still enforce it later. A waiver is only valid in writing and does not cover a later breach."
            )}
          </li>
          <li>
            {t(
              "termsBoilerplateSever",
              "If a court strikes out one clause, the rest stay in force. These terms do not create a partnership, agency or employment."
            )}
          </li>
        </ul>

        <h2>{t("termsLawTitle", "Governing law")}</h2>
        <p>
          {t(
            "termsLawBody",
            "These terms are governed by the laws of the Grand Duchy of Luxembourg, without affecting mandatory protections of your country of residence. If there is a dispute, we will first try to resolve it in good faith within 60 days. After that, courts of Luxembourg have jurisdiction unless a mandatory rule says otherwise."
          )}
        </p>

        <h2>{t("termsContactTitle", "Contact")}</h2>
        <ul>
          <li>
            <strong>{t("termsContactClubLabel", "Your club")}:</strong> {club}
          </li>
          <li>
            <strong>Ping4All:</strong>{" "}
            <a href={`mailto:${PRIVACY_CONTACT}`}>{PRIVACY_CONTACT}</a>
          </li>
        </ul>

        <p className="terms-page__full">
          <Link to="/privacy">
            {t("termsPrivacyCta", "Read the privacy notice for how data is used.")}
          </Link>{" "}
          <Link to="/eula">{t("eulaShort", "Mobile app EULA")}</Link>.
        </p>
        <LegalFooter />
      </article>
    </div>
  );
}
